import { Component, Input, EventEmitter, Output, ViewChild, ElementRef, OnChanges } from '@angular/core';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { publicHelpers } from '../../../../globals/helpers/public-helpers';
import { onlyWhitespace, parseAsText } from '../../helpers/helpers';
import { JoiComment } from 'globals/store/tasks.data';
import { WFWorker } from '../../../../globals/store/workforce.data';
import { NullStateData, nullStateImages, AbnormalStateComponent } from '../dashboard/abnormal_state.component';
import { User } from './../../../../globals/store/user.data';
import { Wrapper } from 'src/app/components/wrapper.component';
import { v4 as uuidV4 } from "uuid";
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PhotoComponent } from 'src/app/components/photo';
import { OnlyWhitespacePipe } from 'src/app/services/pipes/onlyWhitespace.pipe';
import { TitleIconComponent } from "src/app/components/icon/title-icon.component";
import { QuickNoteComponent } from './quick-note.component';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'chat-box',
    templateUrl: './chat-box.html',
    styleUrls: ['./chat-box.sass'],
    imports: [CommonModule, IconComponent, MatIconModule, MatButtonModule, QuickNoteComponent, PhotoComponent, OnlyWhitespacePipe, AbnormalStateComponent, TitleIconComponent],
    standalone: true,
})

export class ChatBoxComponent extends Wrapper<ChatBoxComponent> implements OnChanges {
  helpers = publicHelpers
  @Input() comments: JoiComment[] = []
  @Input() readOnly = true
  @Input() isNotes = false
  @Input() isMessages = false
  @Input() unreadIds: string[]

  @Output() addComment: EventEmitter<JoiComment> = new EventEmitter();
  @Output() quickNotesChanged: EventEmitter<JoiComment[]> = new EventEmitter();
  @Output() messageChanged: EventEmitter<JoiComment> = new EventEmitter()

  newMessage = ''
  workersByEmail: { [key: string]: WFWorker } = {}
  commentGroups: CommentGroup[] = []
  nullStateData: NullStateData = { title: 'Quick Notes', imageClass: nullStateImages.notey, message: 'Use quick notes to jot down or record something from a recent phone conversation or discussion.\n\nQuick notes don’t get published anywhere, they are just stored here for reference.' }
  user: User

  @ViewChild('newCommentDiv') newCommentDiv: ElementRef
  editingNewNote: boolean = false

  constructor() {
    super()
    this.subProp(this.store.select('workforce', 'workersByEmail'), 'workersByEmail')
    this.subProp(this.store.select('user', 'user'), 'user')
  }

  focusNewNote() {
    this.editingNewNote = true
  }

  unfocusNewNote() {
    if (!this.newMessage) this.editingNewNote = false
  }

  cancelNewNote() {
    this.editingNewNote = false
    this.clearMessage()
  }

  ngOnChanges(changes) {
    if (!this.isNotes && changes.comments && changes.comments.currentValue) {
      this.groupComments(changes.comments.currentValue)
      setTimeout(() => document.querySelector(".chat-box").scrollTo(0, 1000000))
    }
  }

  groupComments(comments: JoiComment[]) {
    const commentGroups: CommentGroup[] = []
    if (comments && comments.length) {
      let currentUser
      let lastTime
      let commentGroup: CommentGroup
      comments.forEach(({ content, createdBy, createdAt }) => {
        const seconds = createdAt.hasOwnProperty('seconds') ? createdAt['seconds'] : createdAt.getTime() / 1000
        if (!currentUser || createdBy !== currentUser || !lastTime || seconds - lastTime > 300) {
          if (commentGroup) commentGroups.push(commentGroup)
          commentGroup = {
            comments: [],
            by: createdBy,
            at: new Date(seconds * 1000),
          }
        }
        commentGroup.comments.push(content)
        lastTime = seconds
        currentUser = createdBy
      })
      commentGroups.push(commentGroup)
    }
    this.commentGroups = commentGroups
  }

  commentInput(event) {
    if (event.inputType === 'insertParagraph' && !this.isNotes) {
      if (onlyWhitespace(event.target)) this.clearMessage() // <== this happens when enter is pressed with only whitespace, but also when keyup.enter happens
      else console.log("SHOULD BE SENDING...")
      return
    }
    this.newMessage = parseAsText(event.target)
  }

  clearMessage() {
    this.newCommentDiv.nativeElement.innerHTML = ''
    this.newMessage = ''
  }

  deleteNote(noteIndex: number) {
    const notesCopy = [...this.comments]
    notesCopy.splice(noteIndex, 1)
    // console.log({ notesCopy })
    this.quickNotesChanged.emit(notesCopy)
  }

  saveNote(noteIndex: number, editedNote: string) {
    const note = {...this.comments[noteIndex], content: editedNote, edited: true }
    if (this.isMessages) {
      this.messageChanged.emit(note)
    } else {
      const notesCopy = [...this.comments]
      notesCopy[noteIndex] = note
      this.quickNotesChanged.emit(notesCopy)
    }
  }

  send() {
    if (onlyWhitespace(this.newMessage)) return
    const newComment: JoiComment = {
      content: this.newMessage,
      createdBy: this.userService.email,
      createdAt: new Date(),
      id: uuidV4(),
    }
    if (!this.isNotes || this.isMessages) this.addComment.emit(newComment)
    else this.quickNotesChanged.emit([newComment, ...this.comments])
    this.clearMessage()
    this.editingNewNote = false
  }

}

interface CommentGroup {
  by: string
  at: Date
  comments: string[]
}
